/**
 * QuotaExhaustedOverlay - Overlay displayed inside CoachChat when OpenAI API key runs out of credits
 */

import { AlertCircle, ExternalLink } from 'lucide-react'
import { UnifiedButton } from '../ui/unified-button'

interface QuotaExhaustedOverlayProps {
  isVisible: boolean
  onDismiss: () => void
}

export const QuotaExhaustedOverlay = ({ isVisible, onDismiss }: QuotaExhaustedOverlayProps) => {
  const handleOpenBilling = () => {
    window.open('https://platform.openai.com/account/billing', '_blank', 'noopener,noreferrer')
  }

  if (!isVisible) return null

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center">
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60" onClick={onDismiss} />

      {/* Content card */}
      <div className="bg-background relative mx-4 max-w-md rounded-lg p-6 shadow-lg">
        <div className="">
          {/* Icon and Title */}
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-lg font-semibold">OpenAI Credits Exhausted</h2>
          </div>

          {/* Main message */}
          <p className="text-muted-foreground mb-4 text-sm">
            Your OpenAI API key has run out of credits. To continue using Milo, you need to add
            credits to your OpenAI account.
          </p>

          {/* Instructions */}
          <div className="bg-muted/50 mb-4 space-y-3 rounded-lg p-4">
            <p className="text-sm font-medium">How to add credits:</p>
            <ol className="text-muted-foreground space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="font-medium">1.</span>
                <span>Click the button below to go to OpenAI billing</span>
              </li>
              <li className="flex gap-2">
                <span className="font-medium">2.</span>
                <span>Add a payment method (credit card required)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-medium">3.</span>
                <span>Purchase credits (minimum $10)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-medium">4.</span>
                <span>Credits are available immediately after purchase</span>
              </li>
            </ol>
          </div>

          {/* Note about usage */}
          <p className="text-muted-foreground mb-4 text-xs">
            Note: OpenAI charges based on usage. $10 of credits typically lasts for a very long
            time.
          </p>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <UnifiedButton variant="outline" onClick={onDismiss}>
              Dismiss
            </UnifiedButton>
            <UnifiedButton onClick={handleOpenBilling} className="h-9 flex-1">
              <div className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Go to OpenAI Billing
              </div>
            </UnifiedButton>
          </div>
        </div>
      </div>
    </div>
  )
}
